import { windowFactory } from '../factories/window.factory';

export enum StorageType {
  localStorage = 'localStorage',
  sessionStorage = 'sessionStorage',
}

export interface StorageUtilityOptions {
  storagePrefix: string;
}

const defaultStorageUtilityOptions: StorageUtilityOptions = {
  storagePrefix: 'ghs',
};

export abstract class StorageUtility {
  private windowInstance = windowFactory();
  private storage: Storage;
  protected constructor(
    private storageType: StorageType,
    private storageUtilityOptions: StorageUtilityOptions = defaultStorageUtilityOptions,
  ) {
    this.storage = this.windowInstance[storageType];
  }

  public get(key: string): any {
    key = this.prefix(key);

    let result;
    try {
      result = this.storage.getItem(key);
    } catch (err) {
      console.warn(`${this.storageType} not supported`);
    }
    return result ? this.decode(result) : undefined;
  }

  public set(key: string, value: any): void {
    key = this.prefix(key);
    try {
      this.storage.setItem(key, this.encode(value));
    } catch (err) {
      console.warn(`${this.storageType} not supported`);
    }
  }

  public reset(): boolean {
    const { storagePrefix } = this.storageUtilityOptions;
    let hasReset = false;
    for (const key in localStorage) {
      if (key.indexOf(storagePrefix) === 0) {
        this.remove(key.substring(storagePrefix.length + 1));
        hasReset = true;
      }
    }
    return hasReset;
  }

  public remove(key: string): void {
    key = this.prefix(key);
    try {
      this.storage.removeItem(key);
    } catch (err) {
      console.warn(`${this.storageType} not supported`);
    }
  }

  public dictionary() {
    return this.storage;
  }

  private encode(val: any): string {
    return JSON.stringify(val);
  }

  private decode(val: string): any {
    let rv;
    try {
      rv = JSON.parse(val);
    } catch (e) {
      // Drop it
    }
    return rv;
  }

  private prefix(key: string): string {
    const { storagePrefix } = this.storageUtilityOptions;

    return storagePrefix ? `${storagePrefix}-${key}` : key;
  }

  public isSupported(): boolean {
    // http://diveintohtml5.info/detect.html#storage
    try {
      return (
        this.storageType in this.windowInstance &&
        this.windowInstance[this.storageType] !== null
      );
    } catch (e) {
      return false;
    }
  }
}
